import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { sendContactForm } from './apiService';
import {
  CheckGlyph,
  checkBulletSx,
  eyebrowSx,
  heroPanelSx,
  pageHeadlineSx,
  sectionCardSx,
  subtleCopySx,
} from './designSystem';

const contactDetails = [
  'School or district size',
  'Where you want to pilot first',
  'The kinds of pages basic filters keep missing',
  'Any current allowlist or blocklist structure',
];

const supportTopics = [
  'Pilot design for one campus, grade, or lab',
  'Policy design for loophole or edge-case content',
  'Deployment fit for Chrome, Intune, or Jamf',
];

function Contact() {
  const [messageState, setMessageState] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const name = data.get('name');
    const email = data.get('email');
    const message = data.get('message');

    try {
      setLoading(true);
      await sendContactForm(name, email, message);
      setMessageState({ type: 'success', text: 'Message sent. We will follow up about your school inquiry.' });
      form.reset();
    } catch (error) {
      setMessageState({ type: 'error', text: error.response?.data?.detail || 'Unable to send your message right now.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container sx={{ py: { xs: 6, md: 8 } }}>
      <Box sx={{ ...heroPanelSx, p: { xs: 3.5, md: 6 }, mb: 4 }}>
        <Typography variant="overline" sx={eyebrowSx}>
          Contact
        </Typography>
        <Typography variant="h2" sx={{ ...pageHeadlineSx, mt: 1.5, maxWidth: 760 }}>
          Talk through a pilot or school rollout.
        </Typography>
        <Typography sx={{ ...subtleCopySx, mt: 2, maxWidth: 620, fontSize: '1.05rem' }}>
          Real replies from the team that builds the product — not a ticket queue.
        </Typography>
      </Box>

      <Grid container spacing={4} alignItems="stretch">
        <Grid item xs={12} md={5}>
          <Stack spacing={3} sx={{ height: '100%' }}>
            <Card sx={sectionCardSx}>
              <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                <Typography variant="h5" sx={{ color: '#0B2E43', mb: 2 }}>
                  Helpful details to include
                </Typography>
                <Stack spacing={1.5}>
                  {contactDetails.map((item) => (
                    <Stack key={item} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box sx={checkBulletSx}>
                        <CheckGlyph />
                      </Box>
                      <Typography sx={{ color: '#3A5A6A', lineHeight: 1.6 }}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ ...sectionCardSx, flex: 1 }}>
              <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                <Typography variant="h5" sx={{ color: '#0B2E43', mb: 2 }}>
                  What we help with
                </Typography>
                <Stack spacing={1.5}>
                  {supportTopics.map((item) => (
                    <Box
                      key={item}
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderRadius: '12px',
                        bgcolor: '#F7FAFC',
                        border: '1px solid #E6EDF2',
                      }}
                    >
                      <Typography sx={{ color: '#0B2E43', fontWeight: 650, fontSize: '0.94rem' }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ ...sectionCardSx, height: '100%' }}>
            <CardContent sx={{ p: { xs: 3, md: 4.5 } }}>
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: 'rgba(19,148,127,0.1)',
                        color: '#13947F',
                      }}
                    >
                      <ForumOutlinedIcon />
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ color: '#0B2E43' }}>
                        Send a message
                      </Typography>
                      <Typography sx={{ color: '#52677A', fontSize: '0.9rem', mt: 0.25 }}>
                        We usually reply within one school day.
                      </Typography>
                    </Box>
                  </Stack>
                  {messageState.text && <Alert severity={messageState.type}>{messageState.text}</Alert>}
                  <TextField fullWidth label="Name" name="name" />
                  <TextField fullWidth label="School email" name="email" type="email" />
                  <TextField
                    fullWidth
                    label="How can we help?"
                    name="message"
                    multiline
                    minRows={5}
                    placeholder="Tell us about the pilot environment, the policy problem, or the rollout questions you want to solve first."
                  />
                  <Button type="submit" variant="contained" color="primary" size="large" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Contact;
